import { useCallback, useEffect, useMemo, useState } from 'react';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { getLoadPending, getPreferenceObj } from 'app-components/flux/preferences-v2/preferencesV2Selectors';
import { useAppDispatch, useAppSelector } from 'app-components/hooks/redux';
import { tracker } from 'app-components/utils/core';

import type { AiChatConversationContext } from './aiChatContextTypes';
import { openChat } from './aiChatSlice';
import type { ChatOpenConversationMode } from './aiChatTypes';
import { isAiContextualPromptChipsPreferenceEnabled } from './isAiContextualPromptChipsPreferenceEnabled';
import { DEFAULT_PROMPT_COUNT, PROMPT_CHIP_POOL_SIZE } from './prompts/aiChatPromptConstants';
import type { AiChatPrompt, AiChatPromptSetId } from './prompts/aiChatPromptTypes';
import { getRandomAiChatPrompts } from './prompts/aiChatPromptUtils';
import type { ConversationScopeMode } from './prompts/buildConversationScopeKey';
import { buildConversationScopeKey } from './prompts/buildConversationScopeKey';
import { buildChipHiddenContext, buildChipUserMessage } from './prompts/enrichPromptWithContext';
import { buildAiContextualPromptChipTrackingProps } from './trackAiContextualPromptChipClick';
import { useAutoEnableAiContextualPromptChips } from './useAutoEnableAiContextualPromptChips';

const DEFAULT_VISIBLE_PROMPT_COUNT = DEFAULT_PROMPT_COUNT;

export type ResolveConversationModeArgs = {
  prompt: AiChatPrompt;
  source: string;
  context?: AiChatConversationContext;
  scopeKey?: string;
};

export type UseAiChatSuggestedPromptsArgs = {
  promptSet: AiChatPromptSetId;
  source: string;
  count?: number;
  context?: AiChatConversationContext;
  /** Override hidden model context when a surface needs custom enrichment. */
  enrichPrompt?: (prompt: AiChatPrompt, context: AiChatConversationContext) => string;
  /** Entity scope requires focus context; page scope uses `source` when no entity focus. */
  conversationScope?: ConversationScopeMode;
  resolveConversationMode?: (
    args: ResolveConversationModeArgs,
  ) => ChatOpenConversationMode | Promise<ChatOpenConversationMode>;
};

export type UseAiChatSuggestedPromptsResult = {
  prompts: AiChatPrompt[];
  isEnabled: boolean;
  onPromptSelect: (prompt: AiChatPrompt) => void;
  /** Re-shuffle from the full prompt pool and update visible chips. */
  refreshPrompts: () => void;
};

export const useAiChatSuggestedPrompts = ({
  promptSet,
  source,
  count = DEFAULT_VISIBLE_PROMPT_COUNT,
  context,
  enrichPrompt,
  conversationScope = 'entity',
  resolveConversationMode,
}: UseAiChatSuggestedPromptsArgs): UseAiChatSuggestedPromptsResult => {
  const dispatch = useAppDispatch();

  useAutoEnableAiContextualPromptChips();

  const aiChatV2Enabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlags(state).get('aiChatV2'));
  const aiContextualPromptChips = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlags(state).get('aiContextualPromptChips'),
  );
  const [gsfScreen] = usePreferenceV2('gsfScreen');
  const [aiEnabled] = usePreferenceV2('aiInsightsOptIn');
  const preferencesLoaded = useAppSelector((state) => !getLoadPending(state));
  const chipsPreferenceValue = useAppSelector((state) => {
    const value = getPreferenceObj(state, 'aiContextualPromptChipsEnabled')?.value;
    return typeof value === 'boolean' ? value : undefined;
  });
  const aiContextualPromptChipsEnabled = isAiContextualPromptChipsPreferenceEnabled(
    preferencesLoaded,
    chipsPreferenceValue,
  );
  const completedOnboarding = gsfScreen === 'completed_gsf';
  const isEnabled =
    !!aiChatV2Enabled &&
    !!aiContextualPromptChips &&
    !!aiEnabled &&
    !!aiContextualPromptChipsEnabled &&
    completedOnboarding;

  const [shuffledPrompts, setShuffledPrompts] = useState(() =>
    getRandomAiChatPrompts(promptSet, PROMPT_CHIP_POOL_SIZE),
  );

  useEffect(() => {
    setShuffledPrompts(getRandomAiChatPrompts(promptSet, PROMPT_CHIP_POOL_SIZE));
  }, [promptSet]);

  const prompts = useMemo(() => shuffledPrompts.slice(0, count), [count, shuffledPrompts]);

  const refreshPrompts = useCallback(() => {
    setShuffledPrompts(getRandomAiChatPrompts(promptSet, PROMPT_CHIP_POOL_SIZE));
  }, [promptSet]);

  const onPromptSelect = useCallback(
    (prompt: AiChatPrompt) => {
      void (async () => {
        const scopeKey = buildConversationScopeKey(source, context, conversationScope);
        const conversationMode = resolveConversationMode
          ? await resolveConversationMode({ prompt, source, context, scopeKey })
          : 'new';

        const userMessage = buildChipUserMessage(prompt, context);
        const hiddenContextText =
          context && enrichPrompt ? enrichPrompt(prompt, context) : buildChipHiddenContext(prompt, context);

        tracker.track(
          tracker.events.AIContextualPromptChipClick,
          buildAiContextualPromptChipTrackingProps({
            prompt,
            source,
            promptSet,
            context,
            conversationMode,
            userMessage,
            scopeKey,
          }),
        );

        dispatch(
          openChat({
            message: userMessage,
            hiddenContext: hiddenContextText ? { text: hiddenContextText, data: { source, promptSet } } : undefined,
            conversationMode,
            autoSend: true,
            enableWebSearch: prompt.useWebSearch ?? false,
          }),
        );
      })();
    },
    [context, conversationScope, dispatch, enrichPrompt, promptSet, resolveConversationMode, source],
  );

  return {
    prompts,
    isEnabled,
    onPromptSelect,
    refreshPrompts,
  };
};
